import { TicketMessage } from '../resources/types/ticketTypes';
import { getAiMemory } from './aiMemoryService';
import { LastTripInfo, tripToAiContext } from './tripService';

const MODEL = 'gpt-4o-mini';

const getKey = () => process.env.REACT_APP_OPENAI_API_KEY || '';

export interface AiSuggestion {
  ukrainianReply: string;
  detectedLanguage: string;
}

const callOpenAI = async (
  messages: { role: string; content: string }[],
  jsonMode = false
) => {
  const key = getKey();
  if (!key) throw new Error('NO_KEY');

  const body: any = {
    model: MODEL,
    messages,
    temperature: 0.7,
  };
  if (jsonMode) body.response_format = { type: 'json_object' };

  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `OpenAI error ${res.status}`);
  }

  const data = await res.json();
  return data.choices[0].message.content as string;
};

export const generateTicketReply = async (
  messages: TicketMessage[],
  subject: string,
  lastTrip?: LastTripInfo | null
): Promise<AiSuggestion> => {
  const memory = getAiMemory();

  const conversation = messages
    .map(m => `${m.sender === 'user' ? 'Клієнт' : 'Підтримка'}: ${m.text}`)
    .join('\n');

  const tripContext = lastTrip ? `\n\n${tripToAiContext(lastTrip)}` : '';

  const systemPrompt = `${memory}${tripContext}

---
Ти — оператор підтримки сервісу Whee. Дій відповідно до правил вище.
${lastTrip ? 'Використовуй дані про останню поїздку клієнта для точних відповідей щодо вартості, тривалості та технічних деталей.' : ''}`;

  const userPrompt = `Тема звернення: "${subject}"

Переписка:
${conversation}

Завдання:
1. Визнач мову останнього повідомлення клієнта.
2. Склади відповідь підтримки УКРАЇНСЬКОЮ мовою (для перевірки оператором).

Відповідай СТРОГО у форматі JSON:
{
  "detectedLanguage": "назва мови англійською (наприклад: English, Deutsch, Spanish)",
  "ukrainianReply": "текст відповіді українською"
}`;

  const raw = await callOpenAI(
    [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt },
    ],
    true
  );

  return JSON.parse(raw) as AiSuggestion;
};

export const translateReply = async (
  ukrainianText: string,
  targetLanguage: string
): Promise<string> => {
  const prompt = `Переклади наступний текст з української мови на ${targetLanguage}.
Поверни ТІЛЬКИ переклад, без пояснень і лапок.

${ukrainianText}`;

  return callOpenAI([{ role: 'user', content: prompt }]);
};

export const hasApiKey = () => Boolean(process.env.REACT_APP_OPENAI_API_KEY);
